import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, Trash2, Plus, Loader2, Pencil } from 'lucide-react';
import { triggerHaptic } from '../../utils/haptics';

const AddressesList = () => {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
  const uid = tgUser?.id;

  const fetchAddresses = async () => {
    try {
      if (!uid) {
        setIsLoading(false);
        return;
      }
      const res = await axios.get(`/api/profile?user_id=${uid}`);
      if (res.data.addresses) {
        setAddresses(res.data.addresses);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Удалить этот адрес?')) return;
    
    // Вызов тактильной отдачи
    if (typeof triggerHaptic === 'function') triggerHaptic('warning');
    
    // Оптимистичное обновление UI
    const oldAddresses = [...addresses];
    setAddresses(addresses.filter(a => a.id !== id));

    try {
      await axios.post(`/api/delete-address/${id}`, { user_id: uid });
    } catch (error) {
      console.error("Ошибка удаления:", error);
      alert("Не удалось удалить адрес");
      // Возврат состояния при ошибке
      setAddresses(oldAddresses);
    }
  };

  const handleEdit = (addr, e) => {
      e.stopPropagation();
      if (typeof triggerHaptic === 'function') triggerHaptic('light');
      // Передача данных в AddAddressPage через state
      navigate('/profile/addresses/add', { state: { addressToEdit: addr } });
  };

  const parseAddress = (addrRaw) => {
      try {
          const parsed = JSON.parse(addrRaw);
          if (parsed && parsed.main_text) return parsed.main_text;
          return addrRaw;
      } catch {
          return addrRaw;
      }
  };

  return (
    <div className="min-h-screen bg-tg-bg pb-24 text-tg-text">
      {/* Шапка */}
      <div className="px-4 pb-4 pt-tg flex items-center sticky top-0 bg-tg-bg/95 backdrop-blur-md z-10 border-b border-gray-100/5">
        <button onClick={() => navigate('/profile')} className="mr-3 text-tg-text p-1 active:opacity-50">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-lg font-bold">Мои адреса</h1>
      </div>

      <div className="p-4 space-y-3">
        {/* Кнопка добавления */}
        <button 
          onClick={() => navigate('/profile/addresses/add')} 
          className="w-full bg-tg-secondary-bg p-4 rounded-xl flex items-center gap-3 active:scale-98 transition-transform border border-transparent shadow-sm"
        >
            <div className="bg-tg-button/10 text-tg-button p-2 rounded-full shrink-0">
                <Plus size={20} />
            </div>
            <div className="text-left flex-1">
                <p className="text-sm font-bold">Добавить новый адрес</p>
            </div>
        </button>

        {isLoading ? (
          <div className="flex justify-center p-10"><Loader2 className="animate-spin text-tg-hint" /></div>
        ) : addresses.length === 0 ? (
          <div className="text-center text-tg-hint text-sm py-10 opacity-60">
            У вас пока нет сохраненных адресов
          </div>
        ) : (
          <div className="space-y-2">
            {addresses.map((addr) => (
              <div 
                key={addr.id} 
                className="bg-tg-secondary-bg p-4 rounded-xl flex items-center justify-between shadow-sm active:bg-black/5 transition-colors"
              >
                <div className="flex items-center gap-3 overflow-hidden flex-1">
                    <div className="text-tg-hint shrink-0">
                        <MapPin size={18} />
                    </div>
                    <span className="text-sm font-medium truncate leading-tight">
                        {parseAddress(addr.address)}
                    </span>
                </div>
                
                <div className="flex items-center gap-1">
                    {/* Кнопка редактирования */}
                    <button 
                        onClick={(e) => handleEdit(addr, e)} 
                        className="p-2 text-tg-button hover:bg-tg-button/10 rounded-full transition-colors active:scale-90"
                    >
                      <Pencil size={18} />
                    </button>
                    {/* Кнопка удаления */}
                    <button 
                        onClick={(e) => handleDelete(addr.id, e)} 
                        className="p-2 text-red-500 hover:bg-red-500/10 rounded-full transition-colors active:scale-90"
                    >
                      <Trash2 size={18} />
                    </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressesList;
